import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import rehypeReact from 'rehype-react';
import { graphql } from 'gatsby';
import './styles.css';

import FeedbackForm from '../components/FeedbackForm';
import FlightLandingPage from '../components/FlightLandingPage';
import MARequirements from '../components/MARequirements';
import MAReleaseNotes from '../components/MAReleaseNotes';
import JetLandingPage from '../components/JetLandingPage';
import MSLandingPage from '../components/MSLandingPage/index';
import LandingPageLayout from '../components/LandingPageLayout';
import InfoReader from '../components/InfoReader';
import SigniantReader from '../components/InfoReader/SigniantApp';
import SigniantAppBetaReader from '../components/InfoReader/SigniantAppBeta';
import FlightReader from '../components/InfoReader/Flight';
import CLIReader from '../components/InfoReader/FlightCLI';
import SDCXReader from '../components/InfoReader/SDCXDownload';
import VersionSwitcher from '../components/VersionSwitcher';
import fgwLocations from '../components/ReusedContent/SigCtlLocations';
import ObjectAgentRequirements from '../components/ReusedContent/ObjectAgentRequirements';
import JetNetworkTable from '../components/ReusedContent/JetNetworkRequirements';
import LocalRequirements from '../components/ReusedContent/LocalRequirements';
import JetNetworkTableEx from '../components/ReusedContent/JetNetworkRequirements/Expanded';
import LocalRequirementsEx from '../components/ReusedContent/LocalRequirements/Expanded';
import MediaShuttleLogs from '../components/ReusedContent/MediaShuttleLogs';
import NasStorage from '../components/ReusedContent/NASStorage';
import SigniantServerHardware from '../components/ReusedContent/SigniantServerHardware';
import SDCXReleaseNotes from '../components/ReusedContent/SDCXReleaseNotes';
import SDCXExternalDomains from '../components/ReusedContent/SDCXExternalDomains';
import MarketoForm from '../components/MarketoForm';
import AutomaticUpdate from '../components/ReusedContent/AutomaticUpdate';
import MainSearch from '../components/MainSearch';
import PubNubRanges from '../components/ReusedContent/pubNubRanges';
import BreadcrumbWrapper from '../components/BreadcrumbWrapper';
import JobBasicSettings from '../components/ReusedContent/JobBasicSettings';
import SDCXOperatingSystems from '../components/ReusedContent/SDCXOperatingSystems';
import GCSCredentials from '../components/ReusedContent/GCSCredentials';
import AWSCredentials from '../components/ReusedContent/AWSCredentials';
import IPerfIntro from '../components/ReusedContent/IPerf/Introduction';
import IPerfSource from '../components/ReusedContent/IPerf/Source';
import IPerfDestination from '../components/ReusedContent/IPerf/Destination';
import IPerfExample from '../components/ReusedContent/IPerf/Example';
import SDCXUninstallWindows from '../components/ReusedContent/SDCXUninstallWindows';
import SDCXUninstallLinux from '../components/ReusedContent/SDCXUninstallLinux';
import SpecialCharacters from '../components/ReusedContent/SpecialCharacters';
import PredictingTransferRate from '../components/ReusedContent/PredictingTransferRate';
import SettingsIcon from '../components/ReusedContent/settingsIcon';
import MoreOptions from '../components/ReusedContent/MoreOptions';
import SDCXTroubleshootingIntro from '../components/ReusedContent/CommServiceTroubleshooting/Introduction';
import SDCXTroubleshootingLinux from '../components/ReusedContent/CommServiceTroubleshooting/OperationalLinux';
import SDCXTroubleshootingWindows from '../components/ReusedContent/CommServiceTroubleshooting/OperationalWindows';
import SDCXTroubleshootingInstallation from '../components/ReusedContent/CommServiceTroubleshooting/Installation';
import fgwJetLocations from '../components/ReusedContent/SDCXSigctl';
import amazonCliServerTable from '../components/ReusedContent/flightServerTables/amazonCli';
import azureCliServerTable from '../components/ReusedContent/flightServerTables/azureCli';
import gcsCliServerTable from '../components/ReusedContent/flightServerTables/gcsCli';
import JetMediaShuttleWorkflowLinking from '../components/ReusedContent/JetMediaShuttleWorkflow/LinkingShuttleJet';
import JetMediaShuttleWorkflowIntroduction from '../components/ReusedContent/JetMediaShuttleWorkflow/Introduction';
import JetMediaShuttleWorkflowSendingTop from '../components/ReusedContent/JetMediaShuttleWorkflow/SendingContent/Top';
import JetMediaShuttleWorkflowSendingBottom from '../components/ReusedContent/JetMediaShuttleWorkflow/SendingContent/Bottom';
import JetMediaShuttleWorkflowReceivingTop from '../components/ReusedContent/JetMediaShuttleWorkflow/ReceivingContent/Top';
import JetMediaShuttleWorkflowReceivingBottom from '../components/ReusedContent/JetMediaShuttleWorkflow/ReceivingContent/Bottom';
import SDCXTroubleshootingIntroEnd from '../components/ReusedContent/CommServiceTroubleshooting/IntroductionEnd';

const Template = (props) => {
    // pathnames where we do not need breadcrumb
    const breadcrumbNotRequierdFor = new Set([
        '/jet/search/',
        '/media-shuttle/search/',
        '/manager-agents/search/',
        '/flight/search/',
        // in production, the trailings backslashes are removed and we need to account for that
        '/jet/search',
        '/media-shuttle/search',
        '/manager-agents/search',
        '/flight/search',
    ]);
    const feedbackNotRequiredFor = new Set([
        '/',
        '/media-shuttle/',
        '/jet/',
        '/manager-agents/',
        '/flight/',
        '/jet/search/',
        '/media-shuttle/search/',
        '/manager-agents/search/',
        '/flight/search/',
        // in production, the trailings backslashes are removed and we need to account for that
        '/flight',
        '/flight/search',
        '/jet',
        '/jet/search',
        '/manager-agents/search',
        '/manager-agents',
        '/media-shuttle',
        '/media-shuttle/search',
    ]);

    const isBreadcrumbRequired = (location) => {
        return !breadcrumbNotRequierdFor.has(location.pathname);
    };

    const isFeedbackRequired = (location) => {
        return !feedbackNotRequiredFor.has(location.pathname);
    };

    const renderAst = new rehypeReact({
        createElement: React.createElement,
        components: {
            // add components to this
            // 'what you want to call it': WhatYouImportedItAs,
            // Marketo Form
            'marketo-form': MarketoForm,
            // Landing Page Cards
            'landing-page-layout': LandingPageLayout,
            //Flight Gateway
            'flight-landing-page': FlightLandingPage,
            'amazon-cli-server-table': amazonCliServerTable,
            'azure-cli-server-table': azureCliServerTable,
            'gcs-cli-server-table': gcsCliServerTable,
            // M+A
            'landing-ma-requirements': MARequirements,
            'landing-ma-release-notes': MAReleaseNotes,
            // Jet
            'jet-landing-page': JetLandingPage,
            // Media Shuttle
            'ms-landing-page': MSLandingPage,
            // Download Information Readers
            'info-reader': InfoReader,
            'signiant-reader': SigniantReader,
            'signiant-beta-reader': SigniantAppBetaReader,
            'flight-reader': FlightReader,
            'cli-reader': CLIReader,
            'sdcx-reader': SDCXReader,
            // Reused Content
            'fgw-install': fgwLocations,
            'fgw-install-jet': fgwJetLocations,
            'object-agent-requirements': ObjectAgentRequirements,
            'signiant-server-hardware': SigniantServerHardware,
            'jet-network-table': JetNetworkTable,
            'local-requirements-table': LocalRequirements,
            'media-shuttle-logs': MediaShuttleLogs,
            'nas-storage': NasStorage,
            'sdcx-release-notes': SDCXReleaseNotes,
            'sdcx-external-domains': SDCXExternalDomains,
            'automatic-update': AutomaticUpdate,
            'pubnub-ranges': PubNubRanges,
            'special-characters': SpecialCharacters,
            //expanded port lists
            'jet-network-table-expanded': JetNetworkTableEx,
            'local-requirements-expanded': LocalRequirementsEx,
            'main-search': MainSearch,
            'job-basic-settings': JobBasicSettings,
            'sdcx-operating-systems': SDCXOperatingSystems,
            'gcs-credentials': GCSCredentials,
            'aws-credentials': AWSCredentials,
            //iperf document:
            'i-perf-intro': IPerfIntro,
            'i-perf-source': IPerfSource,
            'i-perf-destination': IPerfDestination,
            'i-perf-example': IPerfExample,
            'predicting-transfer-rate': PredictingTransferRate,
            //troubleshooting comm service:
            'jet-shuttle-workflow-introduction': JetMediaShuttleWorkflowIntroduction,
            'jet-shuttle-workflow-sending-top': JetMediaShuttleWorkflowSendingTop,
            'jet-shuttle-workflow-sending-bottom': JetMediaShuttleWorkflowSendingBottom,
            'jet-shuttle-workflow-receiving-top': JetMediaShuttleWorkflowReceivingTop,
            'jet-shuttle-workflow-receiving-bottom': JetMediaShuttleWorkflowReceivingBottom,
            'jet-shuttle-workflow-linking': JetMediaShuttleWorkflowLinking,
            'comm-service-introduction': SDCXTroubleshootingIntro,
            'comm-service-introduction-end': SDCXTroubleshootingIntroEnd,
            'comm-service-linux': SDCXTroubleshootingLinux,
            'comm-service-windows': SDCXTroubleshootingWindows,
            'comm-service-installation': SDCXTroubleshootingInstallation,
            //
            'sdcx-uninstall-windows': SDCXUninstallWindows,
            'sdcx-uninstall-linux': SDCXUninstallLinux,
            'settings-icon': SettingsIcon,
            'more-options': MoreOptions,
        }, // components
    }).Compiler;

    const { currentPage: page, versionsOfPage: versions, formiumForm: form } = props.data;
    const { location } = props;

    const { breadcrumb } = props.pageContext;
    // Bar appears in the title only if the title is not empty
    const bar = page.frontmatter.title ? ' | ' : '';

    return (
        <>
            {isBreadcrumbRequired(location) && (
                <BreadcrumbWrapper crumbSeparator={' > '} pageTitle={page.frontmatter.title} {...breadcrumb} />
            )}
            <div style={{ margin: '2rem auto', maxWidth: 1200 }}>
                <Helmet title={page.frontmatter.title + bar + 'Signiant Help'} />

                <div className="page">
                    <header>
                        {page.frontmatter.version ? (
                            <VersionSwitcher versions={versions.edges} current={page.frontmatter.path} />
                        ) : null}
                        <h1>{page.frontmatter.title}</h1>
                        <span>{page.frontmatter.baseline}</span>
                    </header>

                    {
                        renderAst(page.htmlAst) // replaces dangerouslySetHTML 👍
                    }
                </div>
                {isFeedbackRequired(location) && <FeedbackForm formSlug={form.slug} {...page.frontmatter} />}
            </div>
        </>
    );
};

Template.propTypes = {
    data: PropTypes.object.isRequired,
    pageContext: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
};

export default Template;

// eslint-disable-next-line no-undef
export const pageQuery = graphql`
    query DocsByPath($path: String!, $type: String!, $formSlug: String!) {
        currentPage: markdownRemark(frontmatter: { path: { eq: $path } }) {
            htmlAst
            frontmatter {
                path
                title
                version
            }
        }
        versionsOfPage: allMarkdownRemark(filter: { frontmatter: { type: { eq: $type } } }) {
            edges {
                node {
                    frontmatter {
                        path
                        version
                    }
                }
            }
        }
        formiumForm(slug: { eq: $formSlug }) {
            slug
        }
    }
`;
